'use client'

import { useLayoutEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import MUIStack from '@mui/material/Stack'
import { useTheme } from '@mui/material/styles'
import { WizardFormInput } from '@repo/ui/WizardFormInput'
import MUIBox from '@mui/material/Box'
import MUIIconButton from '@mui/material/IconButton'
import { AddIcon } from '@repo/ui/AddIcon'
import { ArrowRightIcon } from '@repo/ui/ArrowRightIcon'

const FLIP_TRANSITION = 'transform 250ms ease'

export const DocumentBundleSection = ({
  section,
  isFirst,
  isLast,
  isEditMode,
  error,
  onNameChange,
  onMoveUp,
  onMoveDown,
  onDelete
}) => {
  const theme = useTheme()
  // Bundledocs auto-creates its own default section on bundle creation, so "must keep one section"
  // only applies once the bundle actually exists (edit mode) - freely deletable in create mode.
  const disableDelete = isEditMode && isFirst && isLast

  const rowRef = useRef<HTMLDivElement>(null)
  const previousTopRef = useRef<number | null>(null)

  // FLIP: capture this row's position just before the reorder, then once it re-renders at its
  // new slot, offset it back to where it was and transition to zero - so the row that was
  // actually clicked visibly slides to its new position instead of the reorder just appearing
  // instantly (which also reads as the neighbouring row having moved instead).
  useLayoutEffect(() => {
    const node = rowRef.current
    if (!node || previousTopRef.current === null) return

    const delta = previousTopRef.current - node.getBoundingClientRect().top
    previousTopRef.current = null
    if (!delta) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

    node.style.transition = 'none'
    node.style.transform = `translateY(${delta}px)`

    requestAnimationFrame(() => {
      node.style.transition = FLIP_TRANSITION
      node.style.transform = 'translateY(0)'
    })

    const clearInlineStyles = () => {
      node.style.transition = ''
      node.style.transform = ''
    }
    node.addEventListener('transitionend', clearInlineStyles, { once: true })
  })

  const reorder = (moveFn: () => void) => {
    if (rowRef.current) {
      previousTopRef.current = rowRef.current.getBoundingClientRect().top
    }
    moveFn()
  }

  const handleMoveDown = () => reorder(() => onMoveDown(section.id))
  const handleMoveUp = () => reorder(() => onMoveUp(section.id))

  return (
    <MUIBox
      ref={rowRef}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 4,
        width: '100%'
      }}
    >
      <WizardFormInput
        name={`section-${section.id}`}
        label="Section name"
        required
        value={section.name}
        onChange={(value) => onNameChange(section.id, value)}
        errors={error ? { [`section-${section.id}`]: error } : {}}
        sx={{ flex: 1, flexGrow: 1 }}
      />

      <MUIStack
        direction="row"
        alignItems="center"
        justifyContent="center"
        gap={3}
        sx={{
          flexShrink: 1,
          minWidth: '50px',
          height: '56px',
          mt: '0px',
          marginRight: '-45px'
        }}
      >
        <MUIIconButton
          size="small"
          onClick={handleMoveDown}
          disabled={isLast}
          sx={{
            border: '1px solid',
            borderColor: theme.palette.primary.main,
            opacity: isLast ? theme.palette.action.disabledOpacity : 1
          }}
          title="Move section down"
        >
          <ArrowRightIcon
            sx={{
              width: 16,
              height: 16,
              transform: 'rotate(90deg)',
              color: theme.palette.primary.main
            }}
          />
        </MUIIconButton>

        <MUIIconButton
          size="small"
          onClick={handleMoveUp}
          disabled={isFirst}
          sx={{
            border: '1px solid',
            borderColor: theme.palette.primary.main,
            opacity: isFirst ? theme.palette.action.disabledOpacity : 1
          }}
          title="Move section up"
        >
          <ArrowRightIcon
            sx={{
              width: 16,
              height: 16,
              transform: 'rotate(-90deg)',
              color: theme.palette.primary.main
            }}
          />
        </MUIIconButton>
      </MUIStack>

      <MUIIconButton
        sx={{
          right: '-25px',
          top: '28px',
          transform: 'translate(50%, -50%) rotate(45deg)',
          backgroundColor: 'background.default',
          zIndex: 1
        }}
        onClick={() => onDelete(section.id)}
        disabled={disableDelete}
        title="Remove section"
      >
        <AddIcon sx={{ width: '24px', height: '24px' }} />
      </MUIIconButton>
    </MUIBox>
  )
}
DocumentBundleSection.propTypes = {
  section: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
    order: PropTypes.number
  }).isRequired,
  isFirst: PropTypes.bool,
  isLast: PropTypes.bool,
  isEditMode: PropTypes.bool,
  error: PropTypes.string,
  onNameChange: PropTypes.func.isRequired,
  onMoveUp: PropTypes.func.isRequired,
  onMoveDown: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired
}

DocumentBundleSection.defaultProps = {
  isFirst: false,
  isLast: false,
  isEditMode: false,
  error: ''
}